'use client'

// The library couldn't be read — a transport blip or a 5xx, not a refusal.
// Sibling of LibraryAccessDeniedNotice: that one reports the server's answer,
// this one reports that there wasn't one. It stays a strip rather than a full
// state because whatever rows were already on screen remain usable; the point
// is to stop an unreachable library from reading as an empty one.

import { RefreshCw } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useLibraryStore } from '@/store/libraryStore'

export function LibraryLoadFailedNotice({ className }: { className?: string }) {
  const loadFailed = useLibraryStore(s => s.loadFailed)
  // Store-level, not local state: retryLoad clears `loadFailed` while the
  // refetch runs, and this flag is what keeps the strip (and its spinner)
  // visible through that window instead of vanishing on click.
  const retrying = useLibraryStore(s => s.retrying)
  const retryLoad = useLibraryStore(s => s.retryLoad)

  if (!loadFailed && !retrying) return null

  return (
    <div
      role="status"
      className={cn(
        'flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-3 py-2',
        className,
      )}
    >
      <p className="min-w-0 text-xs text-muted-foreground">
        We couldn’t reach your library. What’s shown may be out of date.
      </p>
      <Button size="sm" variant="ghost" className="h-7 shrink-0" onClick={() => void retryLoad()} disabled={retrying}>
        <RefreshCw className={cn('h-3.5 w-3.5', retrying && 'animate-spin')} />
        {retrying ? 'Retrying…' : 'Try again'}
      </Button>
    </div>
  )
}
